(ns practitest.frontend.components2.dashboard.charts.markers
  (:require ["d3" :as d3]))

(defn- nearest-category [x-scale x-type categories mouse-x]
  (if (= x-type :temporal)
    (apply min-key #(js/Math.abs (- mouse-x (x-scale %))) categories)
    (let [half-bw (/ (.bandwidth x-scale) 2)]
      (apply min-key
             (fn [cat] (js/Math.abs (- mouse-x (+ (x-scale cat) half-bw))))
             categories))))

(defn render!
  "Attaches hover markers (circles) for stacked area-like charts.

   y-fn:     (fn [^js layer cat-idx]) -> y pixel for the marker on that layer
   color-fn: (fn [^js layer])         -> fill color string for that layer
   x-type:   (optional) :temporal to skip .bandwidth calls on the x scale
   x-format: (optional) d3 time format string used to label temporal categories
             in the tooltip (defaults to \"%Y-%m-%d\" when x-type is :temporal)"
  [{:keys [marks-g x-scale x-type x-format y-fn color-fn categories layers
           inner-width baseline-y tooltip tooltip-spec]}]
  (-> marks-g (.select "g.hover-markers") .remove)
  (-> marks-g (.select "rect.hover-overlay") .remove)

  (let [cat-index  (into {} (map-indexed (fn [i cat] [cat i]) categories))
        ;; For temporal: cat is a ms-since-epoch number → js/Date. converts it correctly.
        ;; For nominal: cat is a string → str is identity.
        format-cat (if (= x-type :temporal)
                     (fn [cat] ((.utcFormat d3 (or x-format "%Y-%m-%d")) cat))
                     str)
        markers-g  (-> marks-g
                       (.append "g")
                       (.attr "class" "hover-markers")
                       (.style "display" "none"))
        circles    (-> markers-g
                       (.selectAll "circle.hover-marker")
                       (.data layers (fn [^js layer] (.-key layer)))
                       (.enter)
                       (.append "circle")
                       (.attr "class" "hover-marker")
                       (.attr "r" 5)
                       (.attr "stroke" "white")
                       (.attr "stroke-width" 1.5)
                       (.attr "fill" color-fn)
                       (.style "pointer-events" "none"))
        overlay    (-> marks-g
                       (.append "rect")
                       (.attr "class" "hover-overlay")
                       (.attr "x" 0)
                       (.attr "y" 0)
                       (.attr "width" inner-width)
                       (.attr "height" baseline-y)
                       (.attr "fill" "none")
                       (.style "pointer-events" "all"))]

    (-> overlay
        (.on "mousemove"
             (fn [^js event]
               (let [pointer   (.pointer d3 event (.node marks-g))
                     mouse-x   (aget pointer 0)
                     mouse-y   (aget pointer 1)
                     cat       (nearest-category x-scale x-type categories mouse-x)
                     cat-idx   (get cat-index cat)
                     cx        (if (= x-type :temporal)
                                 (x-scale cat)
                                 (+ (x-scale cat) (/ (.bandwidth x-scale) 2)))]
                 (.style markers-g "display" nil)
                 (.attr circles "cx" cx)
                 (.attr circles "cy" (fn [^js layer] (y-fn layer cat-idx)))
                 (.style circles "display"
                         (fn [^js layer]
                           (let [pt (aget layer cat-idx)]
                             (if (> (- (aget pt 1) (aget pt 0)) 0)
                               nil
                               "none"))))
                 (when-let [{:keys [show!]} tooltip]
                   (let [sort-mode     (or (:sort tooltip-spec) :none)
                         layers-seq    (array-seq layers)
                         nearest-layer (apply min-key
                                              (fn [^js layer]
                                                (js/Math.abs
                                                  (- mouse-y (y-fn layer cat-idx))))
                                              layers-seq)
                         base-rows     (->> layers-seq
                                            (mapv (fn [^js layer]
                                                    (let [pt (aget layer cat-idx)]
                                                      {:label (.-key layer)
                                                       :value (- (aget pt 1) (aget pt 0))
                                                       :color (color-fn layer)})))
                                            (filterv #(pos? (:value %))))
                         rows          (case sort-mode
                                         :asc  (sort-by :value < base-rows)
                                         :desc (sort-by :value > base-rows)
                                         base-rows)
                         total         (reduce + 0 (map :value rows))
                         category-text (if (:show-total? tooltip-spec)
                                         (if (pos? total)
                                           (str (format-cat cat) " > Total: " total)
                                           (str (format-cat cat) " > No data"))
                                         (format-cat cat))]
                     (show! {:category category-text
                             :rows     rows
                             :anchor-x cx
                             :anchor-y (y-fn nearest-layer cat-idx)}))))))
        (.on "mouseleave"
             (fn [^js _event]
               (.style markers-g "display" "none")
               (when-let [{:keys [hide!]} tooltip]
                 (hide!)))))))
